/**
 * API Service — centralized REST API client for stock-ai-service backend.
 * Replaces fileSystemHelper for data persistence operations.
 * Handles JWT token management and unified error handling.
 *
 * Plan C (2026-05-16): access + refresh token. apiCall transparently retries
 * once after 401 by calling /api/auth/refresh. Concurrent 401s share a single
 * in-flight refresh via the `refreshInFlight` promise.
 */

import { log } from '../utils/logging';
import { withRetry } from '../utils/retry';

const AI_SERVICE_URL = process.env.REACT_APP_AI_SERVICE_URL || 'http://localhost:8080';

// 5xx infrastructure-side transient statuses. We deliberately exclude 500
// (often a genuine server bug, not transient) and all 4xx (client errors —
// 401 is handled separately via refresh).
const RETRIABLE_STATUSES = [502, 503, 504];

// Per-method default retry budget. GET is idempotent → safe to retry on
// transient failure. POST/PUT/DELETE may have side effects (saveScan,
// AI calls that cost tokens) → default 0 retries; callers opt-in if needed.
const defaultRetryFor = (method) => ({
  retries: method === 'GET' ? 2 : 0,
  delayMs: 1000,
  backoff: 2,
});

// shouldRetry decision: retriable HTTP status was marked explicitly, OR the
// fetch itself rejected (TypeError = network-layer failure: DNS, offline,
// IPv6 hang, mid-deploy connection refused). 4xx and 5xx not in RETRIABLE
// fall through here as plain Error and are NOT retried.
const shouldRetryApiError = (err) =>
  err.retriable === true || err.name === 'TypeError';

// ==========================================
// Token Management
// ==========================================

const TOKEN_KEY = 'stock_ai_jwt_token';            // legacy name, holds access token
const REFRESH_TOKEN_KEY = 'stock_ai_refresh_token';
const USERNAME_KEY = 'stock_ai_username';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
export const setRefreshToken = (token) => localStorage.setItem(REFRESH_TOKEN_KEY, token);
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USERNAME_KEY);
};
export const getUsername = () => localStorage.getItem(USERNAME_KEY);
export const setUsername = (username) => localStorage.setItem(USERNAME_KEY, username);
export const isAuthenticated = () => !!getToken();

// ==========================================
// HTTP Client (with auto-refresh on 401)
// ==========================================

const authHeaders = () => {
  const token = getToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// In-flight refresh promise. Concurrent 401s wait for this rather than each
// firing their own /refresh request.
let refreshInFlight = null;

const refreshAccessToken = async () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      // Refresh token is the HttpOnly cookie — browser sends it automatically
      // via credentials:'include'; no body needed.
      const response = await fetch(`${AI_SERVICE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.status === 401 || response.status === 403) {
        // Cookie missing/expired → permanent, must re-login.
        const err = new Error('Refresh failed: refresh cookie invalid or expired');
        err.authExpired = true;
        throw err;
      }
      if (!response.ok) {
        // Transient (5xx / network) — let withRetry re-attempt the whole call.
        const err = new Error(`Refresh failed transiently: ${response.status}`);
        err.retriable = true;
        throw err;
      }
      const data = await response.json();
      setToken(data.accessToken);
      return data.accessToken;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

const sendRequest = async (method, url, headers, body, options) => {
  return fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
    signal: options.signal,
    credentials: 'include',   // send HttpOnly refresh cookie (path-scoped to /api/auth/refresh)
  });
};

const apiCall = async (method, path, body = null, options = {}) => {
  const url = `${AI_SERVICE_URL}${path}`;
  const retryOpts = options.retry || defaultRetryFor(method);
  const buildHeaders = () => ({
    ...authHeaders(),
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {}),
  });

  return withRetry(async () => {
    let response = await sendRequest(method, url, buildHeaders(), body, options);

    // Try to silently refresh on 401 (access token expired). Skip for the
    // refresh endpoint itself to avoid recursion. If refresh itself fails
    // (network/502/etc.) we re-throw and withRetry decides whether to
    // attempt the whole apiCall again — this is how transient deploy /
    // IPv6 hiccups during /api/auth/refresh stop booting the user.
    if (response.status === 401 && !path.startsWith('/api/auth/')) {
      try {
        await refreshAccessToken();
        response = await sendRequest(method, url, buildHeaders(), body, options);
      } catch (refreshErr) {
        log.error('AUTH_REFRESH', refreshErr);
        // Permanent: refresh cookie missing/expired → log out immediately.
        if (refreshErr.authExpired) {
          clearToken();
          window.dispatchEvent(new Event('auth-expired'));
          throw new Error('Authentication expired. Please log in again.');
        }
        // Transient (fetch network error, 502 mid-deploy, etc.) — mark
        // retriable so withRetry can re-attempt the whole apiCall.
        refreshErr.retriable = true;
        throw refreshErr;
      }
    }

    // Genuine forbidden (post-refresh or refresh endpoint itself) → boot.
    if (response.status === 401 || response.status === 403) {
      clearToken();
      window.dispatchEvent(new Event('auth-expired'));
      throw new Error('Authentication expired. Please log in again.');
    }

    // Transient 5xx → throw with retriable marker so withRetry retries.
    if (RETRIABLE_STATUSES.includes(response.status)) {
      const err = new Error(`API error ${response.status}`);
      err.retriable = true;
      throw err;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error ${response.status}: ${errorText}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return response.text();
  }, { ...retryOpts, shouldRetry: shouldRetryApiError })
    .catch((err) => {
      // After all retries exhausted, surface a stable auth-expired event
      // for callers that started as a refresh failure.
      if (err.message && err.message.startsWith('Refresh failed')) {
        clearToken();
        window.dispatchEvent(new Event('auth-expired'));
        throw new Error('Authentication expired. Please log in again.');
      }
      throw err;
    });
};

const get = (path, options) => apiCall('GET', path, null, options);
const post = (path, body, options) => apiCall('POST', path, body, options);
const del = (path, options) => apiCall('DELETE', path, null, options);

// ==========================================
// Auth
// ==========================================

export const login = async (username, password) => {
  const response = await apiCall('POST', '/api/auth/login', { username, password });
  // Refresh token now arrives as an HttpOnly cookie (Set-Cookie), not in the
  // body — nothing to store for it. Keep only access token + username.
  const accessToken = response.accessToken || response.token;
  setToken(accessToken);
  setUsername(response.username);
  return response;
};

export const getMe = () => get('/api/auth/me');

export const logout = () => {
  clearToken();
  window.dispatchEvent(new Event('auth-expired'));
  // Best-effort: clear the server-side refresh cookie too (fire-and-forget).
  fetch(`${AI_SERVICE_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' })
    .catch((e) => log.error('LOGOUT', e));
};


// ==========================================
// Profile (trading_insights)
// ==========================================

export const getProfile = () => get('/api/profile');

export const saveProfile = (profileData, quickStats, coverage) =>
  post('/api/profile', { profileData, quickStats, coverage });

export const mergeProfile = (reviewData, sourceFileName, reviewDate) =>
  post('/api/profile/merge', { reviewData, sourceFileName, reviewDate });

// Merge one or more saved reviews (by id) into the user's profile + coverage.
// mode: "append" (default), "rebuild" (clear first), or "replace" (clear but preserve
// original mergedAt for matching sources). Backend handles both profile merge and
// coverage update server-side in one transaction.
export const mergeReviewsIntoProfile = (reviewIds, mode = 'append') =>
  post('/api/profile/merge-reviews', { reviewIds, mode });

// ==========================================
// Market
// ==========================================

export const getMarketContext = () => get('/api/market/context');

// ==========================================
// AI Scan (Claude / OpenAI / Risk Assessment)
// ==========================================
// Default 0 retries (POST). AI calls cost tokens — auto-retry risks
// double-spending if backend's downstream call to Anthropic/OpenAI partly
// completed. Caller manually retrying via UI is preferred. Opt-in retry
// is still available via { retry: {...} } if needed.

export const sendClaudeAnalysis = (payload) => post('/api/scan/claude', payload);
export const sendOpenaiAnalysis = (payload) => post('/api/scan/openai', payload);
export const fetchRiskAssessment = (payload) => post('/api/scan/risk', payload);

// ==========================================
// Trade Records
// ==========================================

export const getTradeRecord = (date) =>
  get(`/api/trades/records?date=${date}`);

export const saveTradeRecord = (date, data) =>
  post('/api/trades/records', { date, data: typeof data === 'string' ? data : JSON.stringify(data) });

export const deleteTradeRecord = (date) =>
  del(`/api/trades/records?date=${date}`);

export const listTradeRecords = (startDate, endDate) =>
  get(`/api/trades/records/list?startDate=${startDate}&endDate=${endDate}`);

// ==========================================
// Enriched Trades
// ==========================================

export const getEnrichedTrades = (startDate, endDate) =>
  get(`/api/trades/enriched?startDate=${startDate}&endDate=${endDate}`);

export const saveEnrichedTrade = (date, data) =>
  post('/api/trades/enriched', { date, data: typeof data === 'string' ? data : JSON.stringify(data) });

// ==========================================
// Broker Imports
// ==========================================

export const getBrokerImport = (month) =>
  get(`/api/trades/broker?month=${month}`);

export const saveBrokerImport = (month, brokerName, data) =>
  post('/api/trades/broker', { month, brokerName, data: typeof data === 'string' ? data : JSON.stringify(data) });

export const listBrokerImports = () =>
  get('/api/trades/broker/list');

// ==========================================
// Scans
// ==========================================

/**
 * Save a scan via the new /api/scan/save endpoint (2026-05-17 path A+).
 * Backend builds allRecommendedStocks from the raw AI results — caller does
 * not need to construct the merged picks list.
 *
 * @param {Object} request - {source, marketContext, anthropicResult,
 *                            openaiResult, riskAssessment, scanTimestamp}
 *   marketContext is the analysis-time snapshot (required).
 */
export const saveScan = (request) =>
  post('/api/scan/save', request);

export const getLatestScan = (date) =>
  get(`/api/scan/latest?date=${date}`);

export const listScans = (startDate, endDate) =>
  get(`/api/scan/list?startDate=${startDate}&endDate=${endDate}`);

// ==========================================
// Backtest
// ==========================================

export const getBacktestScans = (date) =>
  get(`/api/backtest/scans?date=${date}`);

export const getBacktestScansRange = (startDate, endDate) =>
  get(`/api/backtest/scans/range?startDate=${startDate}&endDate=${endDate}`);

export const runBacktest = (date, scanIds, force = false) =>
  post('/api/backtest/run', { date, scanIds, force });

export const deleteScan = (scanId) =>
  del(`/api/backtest/${scanId}`);

// ==========================================
// Simulated Results
// ==========================================

export const getSimulatedResults = (date) =>
  get(`/api/simulated?date=${date}`);

export const getSimulatedResultsRange = (startDate, endDate) =>
  get(`/api/simulated?startDate=${startDate}&endDate=${endDate}`);

export const saveSimulatedResult = (scanId, date, data) =>
  post('/api/simulated', {
    scanId,
    date,
    data: typeof data === 'string' ? data : JSON.stringify(data),
  });

// ==========================================
// Reviews
// ==========================================

export const saveReview = (reviewDate, timeRange, dateStart, dateEnd, anthropicResult, openaiResult, extractedProfile) =>
  post('/api/review/save', {
    reviewDate,
    timeRange,
    dateStart,
    dateEnd,
    anthropicResult: typeof anthropicResult === 'string' ? anthropicResult : JSON.stringify(anthropicResult),
    openaiResult: typeof openaiResult === 'string' ? openaiResult : JSON.stringify(openaiResult),
    extractedProfile: typeof extractedProfile === 'string' ? extractedProfile : JSON.stringify(extractedProfile),
  });

export const listReviews = (startDate, endDate) => {
  let path = '/api/review/list';
  if (startDate && endDate) path += `?startDate=${startDate}&endDate=${endDate}`;
  return get(path);
};

export const getReview = (id) => get(`/api/review/${id}`);

export const deleteReview = (id) => del(`/api/review/${id}`);

// ==========================================
// AI Analysis (all consolidated under /api/scan/*, 2026-05-17 path A+)
// ==========================================

export const analyzeWithClaude = (payload) =>
  post('/api/scan/claude', payload);

export const analyzeWithOpenai = (payload) =>
  post('/api/scan/openai', payload);

export const assessRisk = (payload) =>
  post('/api/scan/risk', payload);

export const fetchAiReview = (reviewData) =>
  post('/api/review/analyze', reviewData);

